import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import validate from 'validate.js';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Snackbar,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import PriceToStr from '../../_components/PriceToStr';
import { withRouter } from 'react-router-dom';

import OrderDataBlock from './components/OrderDataBlock';
import DeliveryBlock from './components/DeliveryBlock';
import PaymentBlock from './components/PaymentBlock';
import CommentBlock from './components/CommentBlock';

import { cartActions, deliveryActions } from '../../_actions';
import { deliveryService } from '../../_services/delivery.service';
import { createPurchaseDataLayerPayload } from '../../_helpers/analytics';

const useStyles = makeStyles((theme) => ({
  form: {
    position: 'relative',
    '& .form__field-input': {
      backgroundColor: '#F5F7FB',  
      '& p': {
        bottom: '-31px',
        transform: 'none',
        left: '27px',
        fontSize: '12px',
        width: 'auto',
        right: 'auto',
      }
    },
    '& .form__field-textarea': {
      backgroundColor: '#F5F7FB',  
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 50,
      backgroundColor: '#F5F7FB',
      padding: 0,
      '& fieldset': {
        display: 'none',
      },
      '&:hover fieldset': {
        borderColor: '#FFBE00',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FFBE00',
      },
    },
    '& .MuiInputLabel-outlined, & .MuiFormLabel-root': {
      transform: 'translate(35px, 20px) scale(1)',
      color: '#999999',
    },
    '& .MuiInputLabel-outlined.Mui-focused, & .MuiFormLabel-root.Mui-focused': {
      color: '#FFBE00',
      transform: 'translate(25px, 10px) scale(0.75)',
    },
    '& .MuiInputLabel-shrink, & .MuiFormLabel-root.MuiInputLabel-shrink': {
      transform: 'translate(25px, 10px) scale(0.75)',
      color: '#FFBE00',
    },
    '& .MuiOutlinedInput-input': {
      padding: '25px 25px 10px 35px',
      color: '#777777',
      fontSize: 13,
      lineHeight: '130%',
      fontFamily: 'inherit',
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: '25px 25px 10px 35px',
      minHeight: 56,
    },
    // Autocomplete styles
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: '5px 6px !important',
      '& .MuiAutocomplete-input': {
        padding: '16.5px 14px !important',
        color: '#777777',
        fontSize: 13,
        lineHeight: '1.5',
        fontFamily: 'inherit',
        '&::placeholder': {
          color: '#999999',
          opacity: 1,
        },
      },
      '& .MuiAutocomplete-endAdornment': {
        right: '6px !important',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
      color: '#FFBE00',
    },
    '& .MuiAutocomplete-listbox': {
      fontSize: 13,
      '& .MuiAutocomplete-option': {
        padding: '8px 12px !important',
        color: '#777777',
        '&[aria-selected="true"]': {
          backgroundColor: '#FFF8E1 !important',
          color: '#000',
        },
        '&[data-focus="true"]': {
          backgroundColor: '#FFF8E1 !important',
        },
        '&:hover': {
          backgroundColor: '#FFF8E1 !important',
        },
      },
    },
    '& .MuiRadio-colorPrimary.Mui-checked, & .MuiCheckbox-colorPrimary.Mui-checked': {
      color: '#FFBE00',
    },
    '& .MuiAutocomplete-popupIndicator': {
        marginRight: '20px',
    },
    '& .MuiSvgIcon-root': {
        fill: '#000',
    }

  },
  submitButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    width: '100%',
    backgroundColor: '#EBF0F8',
    border: '1px solid #EBF0F8',
    color: '#1F1F1F',
    borderRadius: 50,
    '&:hover': {
      backgroundColor: '#dce7f0',
    },
    '&.Mui-disabled': {
      backgroundColor: '#ffe6b4 !important',
      color: '#333 !important',
      borderColor: '#f3f5f8',
    },
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  orderSummary: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #eee',
    borderRadius: 8,
    backgroundColor: '#fafbff',
  },
  orderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    fontSize: '15px',
  },
  agreeText: {
    fontSize: '14px',
    color: '#333',
  },}));

/**
 * Валідаційні схеми
 */
const schema = {
  // Основні
  firstName: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 2, maximum: 64 },
  },
  lastName: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 2, maximum: 64 },
  },
  fatherName: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 2, maximum: 64 },
  },
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
  },
  phone: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  // Доставка
  deliveryType: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  deliveryCity: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  // Адреса або Відділення
  addressDelivery: {},
  deliveryDepartment: {},
  // Оплата
  payment: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  edrpou: {},
  comment: {},
};

export const isCompanyInvoicePayment = (payment) => payment === 'invoice';

export const validateEdrpouValue = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue.length < 8 || normalizedValue.length > 10) {
    return ['EDRPOU must be between 8 and 10 characters'];
  }

  return null;
};

const extractArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.data && Array.isArray(value.data)) return value.data;
  if (value.results && Array.isArray(value.results)) return value.results;
  if (value.items && Array.isArray(value.items)) return value.items;
  return [];
};

const mapPaymentToApi = (payment) => {
  switch (payment) {
    case 'liqpay':
    case 'monobank':
    case 'privatbank':
    case 'invoice':
      return 'cashless';
    case 'postpaid':
    case 'cash':
      return 'cash';
    default:
      return payment || 'cash';
  }
};

const getDeliveryTypeName = (deliveryType, deliveryOptions) => {
  const option = extractArray(deliveryOptions).find(
    (item) => String(item.code) === String(deliveryType) || String(item.id) === String(deliveryType),
  );
  return option?.name || '';
};

// Определяем выбранную курьерскую службу для заполнения полей заказа
const getSelectedCourierService = (services, selectedId) =>
  services.find((service) => String(service.id) === String(selectedId));

/**
 * CheckoutForm - головна форма оформлення замовлення
 */
const CheckoutForm = ({
  cartItems,
  cart,
  dispatch,
  listTypeDelivery,
  citiesByDeliveryType,
  addressesByCity,
  listTypePayment,
  createOrderLoading,
  authentication,
  history,
  onPaymentChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getTranslatedRequiredError = (field, defaultMessage) => {
    const requiredTranslations = {
      firstName: 'firstNameRequired',
      lastName: 'lastNameRequired',
      fatherName: 'fatherNameRequired',
      phone: 'phoneRequired',
      email: 'emailRequired',
      deliveryType: 'deliveryRequired',
      deliveryCity: 'deliveryRequired',
      deliveryDepartment: 'deliveryRequired',
      addressDelivery: 'deliveryRequired',
      agreeTerms: 'agreeTermsRequired',
    };

    return requiredTranslations[field]
      ? t(requiredTranslations[field]) || defaultMessage
      : defaultMessage;
  };

  const normalizeValidationErrors = (errors) => {
    const normalizedErrors = { ...errors };

    Object.keys(normalizedErrors).forEach((field) => {
      const fieldErrors = normalizedErrors[field];
      if (!Array.isArray(fieldErrors)) {
        return;
      }

      normalizedErrors[field] = fieldErrors.map((error) => {
        if (typeof error === 'string' && error.toLowerCase().includes('is required')) {
          return getTranslatedRequiredError(field, error);
        }
        return error;
      });
    });

    return normalizedErrors;
  };

  const warehouseId =
    cart?.list?.main_warehouse?.[0]?.warehouse_id ||
    cart?.list?.main_warehouse?.[0]?.warehouse?.id ||
    cartItems?.[0]?.warehouse?.id ||
    '';

  const deliveryOptions = extractArray(listTypeDelivery);

  const courierDeliveryOption = deliveryOptions.find(
    (item) =>
      item.code === 'delivery_service' ||
      item.name?.toLowerCase().includes('курьер') ||
      item.name?.toLowerCase().includes('courier') ||
      item.title?.toLowerCase().includes('курьер') ||
      item.title?.toLowerCase().includes('courier')
  );
  const courierDeliveryTypeValue = courierDeliveryOption
    ? courierDeliveryOption.code || courierDeliveryOption.id
    : '';

  // Стан форми
  const [formState, setFormState] = useState({
    values: {
      firstName: '',
      lastName: '',
      fatherName: '',
      email: '',
      phone: '',
      deliveryType: '',
      deliveryService: '',
      deliveryServiceName: '',
      deliveryCity: '',
      deliveryCityName: '',
      deliveryDepartment: '',
      deliveryDepartmentName: '',
      addressDelivery: '',
      payment: '',
      edrpou: '',
      comment: '',
      agreeTerms: false,
    },
    touched: {},
    errors: {},
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [courierServiceOptions, setCourierServiceOptions] = useState([]);
  const [courierCities, setCourierCities] = useState([]);
  const [courierDepartments, setCourierDepartments] = useState([]);
  const [isLoadingCourierCities, setIsLoadingCourierCities] = useState(false);
  const [isLoadingCourierDepartments, setIsLoadingCourierDepartments] = useState(false);
  const cityOptions =
    formState.values.deliveryType === courierDeliveryTypeValue
      ? courierCities
      : extractArray(citiesByDeliveryType);
  const addressOptions = extractArray(addressesByCity);
  const departmentOptions =
    formState.values.deliveryType === courierDeliveryTypeValue
      ? courierDepartments
      : addressOptions;

  // Завантажити дані при монтуванні
  useEffect(() => {
    dispatch(deliveryActions.getListTypeDelivery(warehouseId));
    dispatch(cartActions.getListTypePayment());
  }, [dispatch, warehouseId]);

  useEffect(() => {
    if (!addressesByCity?.loading) {
      setIsLoadingDepartments(false);
    }
  }, [addressesByCity?.loading]);

  const fetchCourierServices = async () => {
    try {
      const data = await deliveryService.getListDeliveryService();
      const services = extractArray(data);
      setCourierServiceOptions(services);
    } catch (error) {
      setCourierServiceOptions([]);
    }
  };

  const fetchCourierCities = async (serviceId) => {
    if (!serviceId) {
      setCourierCities([]);
      return;
    }

    setIsLoadingCourierCities(true);
    try {
      const data = await deliveryService.getCitiesByDeliveryService(serviceId);
      const results = extractArray(data).map((item) => ({
        id: item.id,
        name: item.name || item.text || item.title,
      }));
      setCourierCities(results);
    } catch (error) {
      setCourierCities([]);
    } finally {
      setIsLoadingCourierCities(false);
    }
  };

  const fetchCourierDepartments = async (cityId) => {
    if (!cityId) {
      setCourierDepartments([]);
      return;
    }

    setIsLoadingCourierDepartments(true);
    try {
      const data = await deliveryService.getDepartmentsByDeliveryCity(cityId, 0, 0, 0, 0, 0);
      const results = extractArray(data).map((item) => ({
        id: item.id,
        name: item.name || item.address || item.title,
        delivery_department_code: item.delivery_department_code || item.id_from_delivery_service_system || '',
        id_from_delivery_service_system: item.id_from_delivery_service_system || item.delivery_department_code || '',
      }));
      setCourierDepartments(results);
    } catch (error) {
      setCourierDepartments([]);
    } finally {
      setIsLoadingCourierDepartments(false);
    }
  };

  const handleDeliveryServiceChange = (serviceId, serviceName = '') => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        deliveryService: serviceId,
        deliveryServiceName: serviceName,
        deliveryCity: '',
        deliveryCityName: '',
        deliveryDepartment: '',
        deliveryDepartmentName: '',
        addressDelivery: '',
      },
    }));
    setCourierCities([]);
    setCourierDepartments([]);
    if (serviceId) {
      fetchCourierCities(serviceId);
    }
  };

  // Валідація
  const hasError = (field) => {
    return formState.touched[field] && formState.errors[field];
  };

  // Обробка змін поля
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [name]: fieldValue,
      },
      touched: {
        ...prevState.touched,
        [name]: true,
      },
    }));

    if (name === 'payment' && onPaymentChange) {
      onPaymentChange(fieldValue);
    }
  };

  // Обробка змін телефону
  const handlePhoneChange = (event) => {
    handleChange({
      target: {
        name: 'phone',
        value: event.target.value,
      },
    });
  };

  // Обчислення суми замовлення
  const getItemPrice = (item) => {
    if (item.price_in_currencies) {
      const currencyPrice = Object.values(item.price_in_currencies)[0];
      if (currencyPrice) {
        const price =
          currencyPrice.price_discount ||
          currencyPrice.price ||
          currencyPrice.regular_price;
        if (price !== undefined && price !== null) {
          return parseFloat(price) || 0;
        }
      }
    }
    const price =
      item.price ||
      item.price_current ||
      item.price_sell ||
      item.unit_price ||
      item.price_total ||
      0;
    return parseFloat(price) || 0;
  };

  const getItemQuantity = (item) => {
    return (
      parseInt(item.quantity || item.count || item.amount || item.qty || 0, 10) || 0
    );
  };

  const cartTotalPrice = cartItems?.reduce((sum, item) => {
    return sum + getItemPrice(item) * getItemQuantity(item);
  }, 0) || 0;

  const postpaidTotalPrice =
    formState.values.payment === 'postpaid'
      ? Math.round(cartTotalPrice * 1.04)
      : cartTotalPrice;

  // Обробка змін типу доставки
  const handleDeliveryTypeChange = (deliveryType) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        deliveryType,
        deliveryService: '',
        deliveryServiceName: '',
        deliveryCity: '',
        deliveryCityName: '',
        deliveryDepartment: '',
        deliveryDepartmentName: '',
        addressDelivery: '',
      },
    }));

    setCourierCities([]);
    setCourierDepartments([]);

    if (deliveryType) {
      if (deliveryType !== courierDeliveryTypeValue) {
        dispatch(deliveryActions.getCityByDeliveryType(deliveryType, warehouseId));
      }
    }
  };

  useEffect(() => {
    if (courierDeliveryTypeValue && !formState.values.deliveryType) {
      handleDeliveryTypeChange(courierDeliveryTypeValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierDeliveryTypeValue]);

  useEffect(() => {
    if (
      formState.values.deliveryType === courierDeliveryTypeValue &&
      courierServiceOptions.length === 0
    ) {
      fetchCourierServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.deliveryType, courierDeliveryTypeValue, courierServiceOptions.length]);

  useEffect(() => {
    if (
      formState.values.deliveryType === courierDeliveryTypeValue &&
      !formState.values.deliveryService &&
      courierServiceOptions.length > 0
    ) {
      const firstService = courierServiceOptions[0];
      handleDeliveryServiceChange(firstService.id, firstService.name || firstService.title || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.deliveryType, formState.values.deliveryService, courierServiceOptions]);

  // Обробка змін міста
  const handleCityChange = (cityId) => {
    const currentCities =
      formState.values.deliveryType === courierDeliveryTypeValue
        ? courierCities
        : cityOptions;
    const selectedCity = currentCities.find((city) => String(city.id) === String(cityId));
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        deliveryCity: cityId,
        deliveryCityName: selectedCity?.name || '',
        deliveryDepartment: '',
        deliveryDepartmentName: '',
        addressDelivery: '',
      },
    }));

    if (cityId && formState.values.deliveryType) {
      if (formState.values.deliveryType === courierDeliveryTypeValue) {
        setIsLoadingCourierDepartments(true);
        fetchCourierDepartments(cityId);
      } else {
        setIsLoadingDepartments(true);
        dispatch(deliveryActions.getAddressByCity(formState.values.deliveryType, cityId));
      }
    }
  };

  const handleDepartmentsChange = (event) => {
    const { name, value } = event.target;
    const selected = departmentOptions.find((item) => String(item.id) === String(value));
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [name]: value,
        deliveryDepartmentName: selected?.name || selected?.address || '',
        addressDelivery: selected ? '' : prevState.values.addressDelivery,
      },
      touched: {
        ...prevState.touched,
        [name]: true,
      },
    }));
  };

  // Валідація форми
  const validateForm = () => {
    let currentSchema = schema;

    // Якщо нема відділень - адреса обов'язкова
    if (
      formState.values.deliveryType &&
      formState.values.deliveryCity &&
      (!departmentOptions || departmentOptions.length === 0)
    ) {
      currentSchema = {
        ...currentSchema,
        addressDelivery: {
          presence: { allowEmpty: false, message: 'is required' },
        },
      };
    } else if (
      formState.values.deliveryType &&
      formState.values.deliveryCity &&
      departmentOptions &&
      departmentOptions.length > 0
    ) {
      currentSchema = {
        ...currentSchema,
        deliveryDepartment: {
          presence: { allowEmpty: false, message: 'is required' },
        },
      };
    }

    let errors = validate(formState.values, currentSchema) || {};

    if (isCompanyInvoicePayment(formState.values.payment)) {
      const edrpouError = validateEdrpouValue(formState.values.edrpou);
      if (edrpouError) {
        errors.edrpou = edrpouError;
      }
    }

    if (!formState.values.agreeTerms) {
      errors.agreeTerms = errors.agreeTerms || [getTranslatedRequiredError('agreeTerms', 'is required')];
    }

    errors = normalizeValidationErrors(errors);

    return Object.keys(errors).length ? errors : null;
  };

  // Функція для збирання даних клієнта в коментарій
  const buildCustomerDataComment = () => {
    const lines = [];
    
    // Особисті дані
    lines.push(`Роздрібне замовлення`);
    lines.push(`Імя: ${formState.values.firstName}`);
    lines.push(`Прізвіще: ${formState.values.lastName}`);
    lines.push(`По батькові: ${formState.values.fatherName}`);
    lines.push(`Номер телефону: ${formState.values.phone}`);
    lines.push(`Email: ${formState.values.email}`);
    
    // Дані доставки
    const deliveryTypeName = getDeliveryTypeName(
      formState.values.deliveryType,
      listTypeDelivery,
    );
    lines.push(`Вид доставки: ${deliveryTypeName}`);
    lines.push(`Місто: ${formState.values.deliveryCityName}`);
    
    // Відділення або адреса
    if (formState.values.deliveryDepartmentName) {
      lines.push(`Відділення: ${formState.values.deliveryDepartmentName}`);
    } else if (formState.values.addressDelivery) {
      lines.push(`Адреса: ${formState.values.addressDelivery}`);
    }
    
    // Коментар клієнта (якщо існує)
    if (formState.values.comment && formState.values.comment.trim()) {
      lines.push(`\nКоментар клієнта: ${formState.values.comment}`);
    } else {
      lines.push(`\nКоментар: Коментар не залишений клієнтом`);
    }
    
    return lines.join('\n');
  };

  // Обробка сабміту
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();

    if (errors) {
      setFormState((prevState) => ({
        ...prevState,
        errors,
        touched: Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
      }));
      setSubmitError(t('fillAllRequiredFields') || 'Заповніть всі обов\'язкові поля');
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      // Збирати дані клієнта для коментаря
      const customerDataComment = buildCustomerDataComment();

      // Підготувати дані для API
      const shoppingCartIds = cartItems?.map((item) => item.id) || [];
      const selectedCourierService = getSelectedCourierService(
        courierServiceOptions,
        formState.values.deliveryService,
      );
      const deliveryTypeName = getDeliveryTypeName(
        formState.values.deliveryType,
        listTypeDelivery,
      );
      const currentDate = new Date();
      const defaultDateScheduled = `${String(currentDate.getDate()).padStart(2, '0')}.${String(
        currentDate.getMonth() + 1,
      ).padStart(2, '0')}.${currentDate.getFullYear()}`;
      const defaultTimeScheduled = `${String(currentDate.getHours()).padStart(2, '0')}:${String(
        currentDate.getMinutes(),
      ).padStart(2, '0')}`;

      const isCourierDelivery = formState.values.deliveryType === courierDeliveryTypeValue;
      const selectedDeliveryDepartment = departmentOptions.find(
        (item) => String(item.id) === String(formState.values.deliveryDepartment)
      );

      const params = {
        session_key: null, // Для гостей
        customer_id: null,
        shopping_cart_id_list: shoppingCartIds,
        delivery_data: {
          type_delivery: formState.values.deliveryType,
          city_id: formState.values.deliveryCity,
          address:
            formState.values.addressDelivery ||
            formState.values.deliveryDepartmentName ||
            formState.values.deliveryDepartment,
          ...(isCourierDelivery && formState.values.deliveryService
            ? {
                delivery_service: formState.values.deliveryService,
                delivery_service_name: formState.values.deliveryServiceName,
              }
            : {}),
        },
        other_order_options: {
          one_time_delivery: 'false',
          delivery_type: formState.values.deliveryType,
          delivery_type_name: deliveryTypeName,
          city_scheduled_delivery: isCourierDelivery ? '' : formState.values.deliveryCity || '',
          id_city_scheduled_delivery: formState.values.deliveryCity || '',
          city_scheduled_delivery_name: isCourierDelivery ? '' : formState.values.deliveryCityName || '',
          address_scheduled_delivery: isCourierDelivery ? '' : formState.values.addressDelivery || '',
          address_scheduled_delivery_name:
            isCourierDelivery
              ? ''
              : formState.values.deliveryDepartmentName || formState.values.addressDelivery || '',
          date_scheduled_delivery: isCourierDelivery ? defaultDateScheduled : '',
          time_scheduled_delivery: isCourierDelivery ? defaultTimeScheduled : '',
          delivery_service: isCourierDelivery ? formState.values.deliveryService || '' : '',
          delivery_service_name: isCourierDelivery ? formState.values.deliveryServiceName || '' : '',
          delivery_service_code: isCourierDelivery
            ? selectedCourierService?.inside_number || selectedCourierService?.code || ''
            : '',
          delivery_city_name: isCourierDelivery ? formState.values.deliveryCityName || '' : '',
          delivery_department_code: isCourierDelivery
            ? selectedDeliveryDepartment?.delivery_department_code || ''
            : '',
          id_from_delivery_service_system: isCourierDelivery
            ? selectedDeliveryDepartment?.id_from_delivery_service_system || ''
            : '',
          delivery_to_address: formState.values.addressDelivery ? 'true' : 'false',
          address: formState.values.addressDelivery || '',
          delivery_recipient_edrpou:
            (mapPaymentToApi(formState.values.payment) === 'cashless')
              ? (formState.values.edrpou && String(formState.values.edrpou).trim() !== ''
                  ? formState.values.edrpou
                  : '00000000')
              : '',
          recipient_last_name_scheduled_delivery: formState.values.lastName,
          recipient_first_name_scheduled_delivery: formState.values.firstName,
          recipient_middle_name_scheduled_delivery: formState.values.fatherName,
          recipient_phone: formState.values.phone,
          recipient_email: formState.values.email,
          recipient_type: 'p',
          company_name: '',
          user_email: formState.values.email,
          return_delivery: '',
          dropshipping: false,
          ttn_phone: '',
          payment: mapPaymentToApi(formState.values.payment),
          code_3PL: '',
          warehouse: warehouseId || '',
        },
        remark: customerDataComment,
      };

      // Відправити замовлення
      const response = await dispatch(
        cartActions.createOrder(shoppingCartIds, params, customerDataComment)
      );

      // Успіх - редирект на сторінку успіху з номером замовлення
      const orderId = response?.order?.id || response?.id;
      const purchasePayload = createPurchaseDataLayerPayload({
        orderId,
        email: formState.values.email,
        phone: formState.values.phone,
        value: response?.value ?? response?.order?.value ?? 0,
        tax: response?.tax ?? 0,
        shipping: response?.shipping ?? 0,
        currency: 'UAH',
        coupon: response?.coupon || '',
        items: (response?.order_items || cartItems || []).map((item) => ({
          product_id: item.product_id || item.id || item.article || '',
          product_group_id: item.product_group_id || item.group_id || '',
          name: item.name || item.item_name || item.title || '',
          price_current: item.price_current ?? item.price ?? item.unit_price ?? 0,
          brand: item.brand || item.item_brand || '',
          category: item.category || item.item_category || '',
          size: item.size || item.size_name || '',
          color: item.color || item.color_name || '',
          quantity: item.quantity || item.qty || 1,
        })),
      });
      
      // Зберігаємо ID замовлення в sessionStorage для валідації на сторінці успіху
      if (orderId) {
        sessionStorage.setItem('lastOrderId', orderId);
        // Встановляємо час дії (15 хвилин)
        sessionStorage.setItem('lastOrderIdTimestamp', Date.now().toString());
        sessionStorage.setItem('lastPurchaseAnalytics', JSON.stringify(purchasePayload));
        
        // Зберігаємо обрану платіжну методу ID для перекладу на сторінці успіху
        const selectedPaymentId = formState.values.payment;
        if (selectedPaymentId) {
          sessionStorage.setItem('lastOrderPaymentId', selectedPaymentId);
        }
      }
      
      history.push(orderId ? `/order-success/${orderId}` : '/orders');
    } catch (error) {
      console.error('Checkout error:', error);
      setSubmitError(
        error?.message || t('errorWhileCreatingOrder') || 'Помилка при створенні замовлення'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && createOrderLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      {/* Alert помилок */}
      {submitError && (
        <Snackbar open={!!submitError} autoHideDuration={6000}>
          <Alert severity="error" className={classes.errorAlert}>
            {submitError}
          </Alert>
        </Snackbar>
      )}

      {/* Блоки форми */}
      <OrderDataBlock
        formState={formState}
        errors={formState.errors}
        handleChange={handleChange}
        handlePhoneChange={handlePhoneChange}
      />

      <DeliveryBlock
        formState={formState}
        errors={formState.errors}
        handleSelectChange={handleChange}
        handleDepartmentsChange={handleDepartmentsChange}
        listDelivery={deliveryOptions}
        listCities={cityOptions}
        listDepartments={departmentOptions}
        onDeliveryTypeChange={handleDeliveryTypeChange}
        onCityChange={handleCityChange}
        isLoadingDepartments={isLoadingDepartments || isLoadingCourierDepartments || isLoadingCourierCities}
        fixedDeliveryType={courierDeliveryTypeValue}
        fixedDeliveryTypeName={courierDeliveryOption?.name || courierDeliveryOption?.title || 'Курьерская служба'}
      />

      <PaymentBlock
        formState={formState}
        errors={formState.errors}
        handleSelectChange={handleChange}
        listTypePayment={listTypePayment}
      />

      <CommentBlock
        formState={formState}
        errors={formState.errors}
        handleChange={handleChange}
      />

      <FormControlLabel
        control={
          <Checkbox
            name="agreeTerms"
            checked={!!formState.values.agreeTerms}
            onChange={handleChange}
            color="primary"
          />
        }
        label={
          <Typography className={classes.agreeText}>
            {t('agreeToTerms') || 'Я погоджуюсь з умовами користувача'}
          </Typography>
        }
      />
      {formState.errors?.agreeTerms && (
        <FormHelperText error className={classes.errorText}>
          {t('agreeTermsError') || 'Потрібно погодитись з умовами'}
        </FormHelperText>
      )}

      {/* Кнопка сабміту */}
      <Button
        variant="contained"
        color="primary"
        className={classes.submitButton}
        type="submit"
        disabled={isLoading || createOrderLoading || !formState.values.agreeTerms}
      >
        {isLoading || createOrderLoading
          ? t('processing') || 'Обробка...'
          : t('placeOrder') || 'Замовити'}
      </Button>
    </form>
  );
};

const mapStateToProps = (state) => ({
  cart: state.cart,
  listTypeDelivery: state.listTypeDelivery,
  citiesByDeliveryType: state.citiesByDeliveryType,
  addressesByCity: state.addressesByCity,
  listTypePayment: state.listTypePayment,
  createOrderLoading: state.createOrder?.loading || false,
  authentication: state.authentication,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CheckoutForm)
);
